/**
 * MCP / tool-call table for a run. One row per tool_use block in the
 * output's assistant message. Each row anchors at #mcp-call-{index} so
 * the transcript's "Called X →" links scroll to the right entry.
 *
 * Cost column is "—" until M3.1 lights up MCP cost emission.
 */
import { useState } from "react";
import type { RunResponse } from "@simpliwork/api-schema";

import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";

type ToolUse = { type: "tool_use"; id: string; name: string; input: unknown };
type ToolResult = { type: "tool_result"; tool_use_id: string; content: unknown };

function summarize(value: unknown, limit = 60): string {
  const stringified = typeof value === "string" ? value : JSON.stringify(value ?? "");
  return stringified.length > limit ? `${stringified.slice(0, limit)}…` : stringified;
}

function findToolResult(run: RunResponse, toolUseId: string): ToolResult | null {
  const inputMessages =
    (run.input as { messages?: Array<{ content?: unknown[] }> } | null)?.messages ?? [];
  for (const msg of inputMessages) {
    for (const block of msg.content ?? []) {
      if (
        block &&
        typeof block === "object" &&
        (block as ToolResult).type === "tool_result" &&
        (block as ToolResult).tool_use_id === toolUseId
      ) {
        return block as ToolResult;
      }
    }
  }
  return null;
}

export function RunMcpTable({ run }: { run: RunResponse }) {
  const content = (run.output as { message?: { content?: unknown[] } } | null)?.message?.content;
  const toolUses: ToolUse[] = Array.isArray(content)
    ? content.filter(
        (b): b is ToolUse =>
          !!b && typeof b === "object" && (b as ToolUse).type === "tool_use",
      )
    : [];

  return (
    <Card className="border-gray-200">
      <CardHeader className="pb-2">
        <CardTitle className="text-sm font-semibold text-gray-700">Tool calls</CardTitle>
      </CardHeader>
      <CardContent>
        {toolUses.length === 0 ? (
          <p className="text-sm text-muted-foreground">No tool calls in this run.</p>
        ) : (
          <ul className="divide-y divide-gray-100">
            {toolUses.map((toolUse, idx) => (
              <McpRow key={toolUse.id} index={idx} toolUse={toolUse} run={run} />
            ))}
          </ul>
        )}
      </CardContent>
    </Card>
  );
}

function McpRow({
  index,
  toolUse,
  run,
}: {
  index: number;
  toolUse: ToolUse;
  run: RunResponse;
}) {
  const [expanded, setExpanded] = useState(false);
  const result = findToolResult(run, toolUse.id);
  const inputSummary = summarize(toolUse.input);
  const outputSummary = result ? summarize(result.content) : "—";

  return (
    <li id={`mcp-call-${index}`}>
      <button
        type="button"
        onClick={() => setExpanded((v) => !v)}
        className="flex w-full items-center justify-between gap-4 py-3 text-left hover:bg-gray-50"
      >
        <div className="flex min-w-0 flex-col gap-0.5">
          <span className="text-sm font-medium text-gray-900">{toolUse.name}</span>
          <span className="truncate text-xs text-muted-foreground">
            in: {inputSummary} → out: {outputSummary}
          </span>
        </div>
        <span className="shrink-0 text-xs text-muted-foreground">—</span>
      </button>
      {expanded && (
        <div className="grid grid-cols-1 gap-3 pb-3 sm:grid-cols-2">
          <div>
            <p className="mb-1 text-xs font-semibold text-muted-foreground">Input</p>
            <pre className="whitespace-pre-wrap rounded border border-gray-200 bg-gray-50 p-2 text-xs">
              {JSON.stringify(toolUse.input, null, 2)}
            </pre>
          </div>
          <div>
            <p className="mb-1 text-xs font-semibold text-muted-foreground">Output</p>
            <pre className="whitespace-pre-wrap rounded border border-gray-200 bg-gray-50 p-2 text-xs">
              {result ? JSON.stringify(result.content, null, 2) : "—"}
            </pre>
          </div>
        </div>
      )}
    </li>
  );
}
